import type { ApiMessageResult } from '../schemas/apiResponse.schemas';

export const PRINT_ACK_MAX_ATTEMPTS = 5;
export const PRINT_ACK_TIMEOUT_MS = 45_000;
export const PRINT_ACK_BACKOFF_MS = 500;

export interface PrintAckAttemptInfo {
  readonly attempt: number;
  readonly maxAttempts: number;
}

export interface AwaitPrintedAckOptions {
  readonly maxAttempts: number;
  readonly timeoutMs: number;
  readonly onAttempt?: (info: PrintAckAttemptInfo) => void;
}

export class PrintAckFailedError extends Error {
  readonly result: ApiMessageResult;

  constructor(result: ApiMessageResult) {
    super(result.message || 'Print ACK not received');
    this.name = 'PrintAckFailedError';
    this.result = result;
  }
}

function isPrintedAck(result: ApiMessageResult): boolean {
  return result.success === true && result.printed === true;
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function errorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return 'Print ACK timeout';
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Print ACK timeout')), timeoutMs);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId);
  }
}

/** Retry `send` until `{ success: true, printed: true }`, then return that result. */
export async function awaitPrintedAck(
  send: () => Promise<ApiMessageResult>,
  options: AwaitPrintedAckOptions,
): Promise<ApiMessageResult> {
  const { maxAttempts, timeoutMs, onAttempt } = options;
  let last: ApiMessageResult = { success: false, message: 'Print ACK not received' };

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    onAttempt?.({ attempt, maxAttempts });
    try {
      last = await withTimeout(send(), timeoutMs);
    } catch (error: unknown) {
      last = { success: false, message: errorMessage(error) };
    }
    if (isPrintedAck(last)) {
      return last;
    }
    if (attempt < maxAttempts) {
      await delay(PRINT_ACK_BACKOFF_MS);
    }
  }

  throw new PrintAckFailedError(last);
}
